import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { ProjectMemberService } from '../lib/projectMemberService';

export interface Notification {
    id: string;
    type: 'like' | 'favorite' | 'follow' | 'challenge_new' | 'challenge_winner' | 'challenge_vote' | 'project_invite' | 'project_join_request';
    actor_id?: string;
    generation_id?: string;
    challenge_id?: string;
    project_id?: string;
    message?: string;
    is_read: boolean;
    created_at: string;
    actor?: {
        username: string;
        avatar_url?: string;
    };
    generation?: {
        img: string;
        prompt: string;
    };
    project?: {
        id: string;
        title: string;
    };
    // For project invites, we also store the membership ID for accept/decline
    membership_id?: string;
}

interface NotificationsContextType {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    fetchNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    clearNotification: (id: string) => Promise<void>;
    // Project invitation actions
    acceptProjectInvitation: (notificationId: string, membershipId: string) => Promise<boolean>;
    declineProjectInvitation: (notificationId: string, membershipId: string) => Promise<boolean>;
    // Project join request actions (for owners)
    approveJoinRequest: (notificationId: string, requesterId: string, projectId: string) => Promise<boolean>;
    declineJoinRequest: (notificationId: string) => Promise<boolean>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);

    const unreadCount = notifications.filter(n => !n.is_read).length;

    const fetchNotifications = useCallback(async () => {
        if (!user) return;

        setLoading(true);
        try {
            // 1. Fetch notifications with project data
            const { data: notifs, error } = await supabase
                .from('notifications')
                .select(`
                  *,
                  generation:generations (
                    image_url,
                    prompt
                  ),
                  project:projects (
                    id,
                    title
                  )
                `)
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(50);

            if (error) throw error;

            if (!notifs || notifs.length === 0) {
                setNotifications([]);
                return;
            }

            // 2. Extract unique actor IDs
            const actorIds = Array.from(new Set(notifs.map((n: any) => n.actor_id).filter(Boolean)));

            // 3. Fetch profiles manually
            let profilesMap: Record<string, any> = {};
            if (actorIds.length > 0) {
                const { data: profiles, error: profilesError } = await supabase
                    .from('profiles')
                    .select('id, username, avatar_url')
                    .in('id', actorIds);

                if (!profilesError && profiles) {
                    profilesMap = profiles.reduce((acc: any, p: any) => {
                        acc[p.id] = p;
                        return acc;
                    }, {});
                }
            }

            // 4. For project_invite notifications, get the membership_id
            const projectInviteNotifs = notifs.filter((n: any) => n.type === 'project_invite' && n.project_id);
            let membershipMap: Record<string, string> = {};
            if (projectInviteNotifs.length > 0) {
                const projectIds = projectInviteNotifs.map((n: any) => n.project_id);
                const { data: memberships } = await supabase
                    .from('project_members')
                    .select('id, project_id')
                    .eq('user_id', user.id)
                    .eq('status', 'pending')
                    .in('project_id', projectIds);

                if (memberships) {
                    memberships.forEach((m: any) => {
                        membershipMap[m.project_id] = m.id;
                    });
                }
            }

            // 5. Merge data
            const transformed = notifs.map((n: any) => ({
                ...n,
                actor: n.actor_id ? profilesMap[n.actor_id] : undefined,
                generation: n.generation ? {
                    img: n.generation.image_url,
                    prompt: n.generation.prompt
                } : undefined,
                project: n.project ? {
                    id: n.project.id,
                    title: n.project.title
                } : undefined,
                membership_id: n.project_id ? membershipMap[n.project_id] : undefined
            }));

            setNotifications(transformed);
        } catch (err) {
            console.error('Error fetching notifications:', err);
        } finally {
            setLoading(false);
        }
    }, [user]);

    const markAsRead = useCallback(async (id: string) => {
        // Optimistic update
        setNotifications(prev => prev.map(n =>
            n.id === id ? { ...n, is_read: true } : n
        ));

        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('id', id);

        if (error) {
            // Revert on error
            setNotifications(prev => prev.map(n =>
                n.id === id ? { ...n, is_read: false } : n
            ));
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        if (!user) return;

        // Optimistic update
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));

        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('user_id', user.id)
            .eq('is_read', false);

        if (error) {
            // Refetch on error
            fetchNotifications();
        }
    }, [user, fetchNotifications]);

    const clearNotification = useCallback(async (id: string) => {
        // Optimistic update
        setNotifications(prev => prev.filter(n => n.id !== id));

        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', id);

        if (error) {
            fetchNotifications(); // Refetch on error
        }
    }, [fetchNotifications]);

    // Accept a project invitation
    const acceptProjectInvitation = useCallback(async (
        notificationId: string,
        membershipId: string
    ): Promise<boolean> => {
        try {
            const success = await ProjectMemberService.acceptInvitation(membershipId);
            if (success) {
                // Mark the notification as read and clear it
                await markAsRead(notificationId);
                setNotifications(prev => prev.filter(n => n.id !== notificationId));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error accepting project invitation:', error);
            return false;
        }
    }, [markAsRead]);

    // Decline a project invitation
    const declineProjectInvitation = useCallback(async (
        notificationId: string,
        membershipId: string
    ): Promise<boolean> => {
        try {
            const success = await ProjectMemberService.declineInvitation(membershipId);
            if (success) {
                // Mark the notification as read and clear it
                await markAsRead(notificationId);
                setNotifications(prev => prev.filter(n => n.id !== notificationId));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error declining project invitation:', error);
            return false;
        }
    }, [markAsRead]);

    // Approve a join request (for project owners)
    const approveJoinRequest = useCallback(async (
        notificationId: string,
        requesterId: string,
        projectId: string
    ): Promise<boolean> => {
        try {
            const success = await ProjectMemberService.approveJoinRequest(requesterId, projectId, notificationId);
            if (success) {
                setNotifications(prev => prev.filter(n => n.id !== notificationId));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error approving join request:', error);
            return false;
        }
    }, []);

    // Decline a join request (for project owners)
    const declineJoinRequest = useCallback(async (
        notificationId: string
    ): Promise<boolean> => {
        try {
            const success = await ProjectMemberService.declineJoinRequest(notificationId);
            if (success) {
                setNotifications(prev => prev.filter(n => n.id !== notificationId));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error declining join request:', error);
            return false;
        }
    }, []);

    // Fetch on mount and user change
    useEffect(() => {
        if (user) {
            fetchNotifications();
        } else {
            setNotifications([]);
        }
    }, [user, fetchNotifications]);

    // Real-time subscription for new notifications
    useEffect(() => {
        if (!user) return;

        const channel = supabase
            .channel('notifications_realtime')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    // Add new notification to the top
                    const newNotification = payload.new as Notification;
                    setNotifications(prev => [newNotification, ...prev]);
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user]);

    return (
        <NotificationsContext.Provider
            value={{
                notifications,
                unreadCount,
                loading,
                fetchNotifications,
                markAsRead,
                markAllAsRead,
                clearNotification,
                acceptProjectInvitation,
                declineProjectInvitation,
                approveJoinRequest,
                declineJoinRequest
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
};
